// Shared constants for the Big J battleship simulator.
//
// Ship-local coordinate frame (voxel grid space, 1 voxel = 1 meter):
//   +X runs stern (0) -> bow (~271), +Y up from the keel, +Z port (0) -> starboard.
// The ship group is centered so world position tracks the ship's center of
// flotation; WATERLINE_Y is the grid height that sits at sea level.

export const GRID_X = 274;   // length cells (270 m hull + padding)
export const GRID_Y = 52;    // keel to masthead
export const GRID_Z = 36;    // 33 m beam + padding

export const SHIP_LEN = 270;
export const HALF_BEAM = 16.5;
export const CENTER_Z = 17;  // hull centerline in grid Z
export const WATERLINE_Y = 9;

// Deck naming: "walk level" is the Y cell your feet occupy on that deck.
export const DECKS = [
  { name: "HOLD / MACHINERY", walk: 3 },
  { name: "THIRD DECK", walk: 8 },
  { name: "SECOND DECK", walk: 11 },
  { name: "MAIN DECK", walk: 14 },
  { name: "01 LEVEL", walk: 17 },
  { name: "02 LEVEL", walk: 20 },
  { name: "03 LEVEL", walk: 23 },
  { name: "NAV BRIDGE", walk: 26 },
];

// ---------------------------------------------------------------------------
// Voxel palette. `solid` blocks movement, `climb` marks ladder cells the
// player and crew ascend, `glass` renders in a separate transparent pass.
// Colors are flat; the mesher shades by face direction and interior dimming.
export const V = {
  AIR: 0, HULL: 1, DECKWOOD: 2, DECKSTEEL: 3, SUPER: 4,
  FLOOR: 5, WALL: 6, ARMOR: 7, LADDER: 8, GLASS: 9,
  MACH: 10, PIPE: 11, TABLE: 12, BUNK: 13, CONSOLE: 14,
  RED: 15, FUNNEL: 16, TURRET: 17, BARREL: 18, WHITE: 19,
  GALLEY: 20, SCREEN: 21, BOTTOM: 22, RAIL: 23, LOCKER: 24,
  GAUGE: 25, CABLE: 26, BRASS: 27,
};

export const VOX = [];
function def(id, color, opts = {}) { VOX[id] = { color, solid: true, ...opts }; }
def(V.AIR, 0x000000, { solid: false });
def(V.HULL, 0x5c6670);          // haze gray hull plating
def(V.DECKWOOD, 0xa8845c);      // holystoned teak weather deck
def(V.DECKSTEEL, 0x4a545e);     // painted steel deck
def(V.SUPER, 0x77828c);         // superstructure haze gray (lighter, catches sun)
def(V.FLOOR, 0x7d5646);         // interior deck: oxblood linoleum
def(V.WALL, 0xc9ccc4);          // interior bulkheads: soothing shipboard white
def(V.ARMOR, 0x3e4750);         // Class A armor: barbettes, belt, conning tower
def(V.LADDER, 0xcfa14e, { solid: false, climb: true }); // brass-gold: "follow the gold ladders"
def(V.GLASS, 0x9fd4e8, { glass: true });
def(V.MACH, 0x4f6b52);          // machinery green
def(V.PIPE, 0x8f7a4e);          // brass / lagged piping
def(V.TABLE, 0x7a5c38);         // furniture wood
def(V.BUNK, 0x8a97a6);          // bunk frames / mattress blue-gray
def(V.CONSOLE, 0x2b3440);       // instrument consoles
def(V.RED, 0xa03828);           // fireplugs, DC gear
def(V.FUNNEL, 0x23272c);        // funnel caps
def(V.TURRET, 0x565f68);        // main battery gun houses
def(V.BARREL, 0x474f57);        // 16" rifles
def(V.WHITE, 0xd8dbd2);         // boats, life rings
def(V.GALLEY, 0x9aa0a8);        // galley stainless
def(V.SCREEN, 0x1c3524);        // dead radar/plot screens (live ones get canvas textures)
def(V.BOTTOM, 0x7c2f2a);        // anti-fouling red below the waterline
def(V.RAIL, 0x39424c, { solid: false, thin: true }); // lifeline stanchions (visual only)
def(V.LOCKER, 0x6e7681);        // ready-service lockers, vents, deck gear
def(V.GAUGE, 0x9fb4c4);         // instrument faces, gauge boards
def(V.CABLE, 0x2f3a44);         // cable trays / conduit runs (wall inlays)
def(V.BRASS, 0xb08d3e);         // binnacle, voice tubes, polished fittings

// Face shading multipliers by normal direction (fully baked; meshes are unlit).
// The "sun" bakes from high on the port bow: +X and -Z faces run warm-bright.
export const FACE_SHADE = { py: 1.0, ny: 0.56, px: 0.88, nx: 0.68, pz: 0.74, nz: 0.84 };
export const INTERIOR_DIM = 0.74;          // skylight factor for enclosed cells
export const INTERIOR_TINT = [1.0, 0.94, 0.82]; // warm "lit by lamps" cast

// ---------------------------------------------------------------------------
// Gunnery. Muzzle velocity is the 16"/50 firing the 1,225 kg HC shell; the
// drag constant is tuned so the range table tops out near the real ~38 km.
export const SHELL_V0 = 800;
export const SHELL_DRAG = 0.000032;
export const GRAVITY = 9.81;
export const SALVO_RELOAD = 25;    // seconds between main battery salvos
export const TURRET_TRAIN_RATE = 4 * Math.PI / 180;  // rad/s
export const TURRET_ELEV_RATE = 12 * Math.PI / 180;

export const KTS = 0.514; // knots -> m/s
